import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Mail, Download, Loader2, Lock, Shield, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Email Capture Modal for Lead Generation
 * 
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: () => void - Called when modal is closed
 * - onSubmit: (email) => void - LEGACY: callback with email only (for backwards compatibility)
 * - onSuccess: (downloadUrl) => void - Called after successful lead capture with download URL
 * - tool: string - Tool identifier for lead capture API (e.g., 'phone-formatter')
 * - fileId: string - The file ID to associate with lead
 * - toolName: string - Human-readable tool name for display
 * - rowsProcessed: number - Optional row count to display
 * - isLoading: boolean - LEGACY: external loading state
 * - accentColor: string - Color variant ('emerald', 'blue', 'purple', 'red', 'indigo')
 */
const EmailCaptureModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  onSuccess,
  tool,
  fileId,
  toolName = 'Free Tool',
  rowsProcessed,
  isLoading: externalLoading,
  accentColor = 'indigo'
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [internalLoading, setInternalLoading] = useState(false);
  const { user } = useAuth();

  const isLoading = externalLoading || internalLoading;

  // Auto-bypass for logged-in users
  useEffect(() => {
    if (isOpen && user?.email && fileId && tool && onSuccess) {
      handleAutoCapture(user.email);
    }
  }, [isOpen, user, fileId, tool]);

  const handleAutoCapture = async (userEmail) => {
    setInternalLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/tools/capture-lead`, {
        email: userEmail,
        tool: tool,
        file_id: fileId
      });
      
      if (response.data.success && onSuccess) {
        onSuccess(response.data.download_url);
        onClose();
      }
    } catch (err) {
      console.error('Auto-capture failed:', err);
      // Fall back to showing modal
      setInternalLoading(false);
    }
  };

  if (!isOpen) return null;

  // Show loading spinner for logged-in users during auto-capture
  if (user?.email && internalLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Preparing your download...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    // If using new lead capture API flow
    if (tool && fileId && onSuccess) {
      setInternalLoading(true);
      try {
        const response = await axios.post(`${API_URL}/api/tools/capture-lead`, {
          email: email.trim().toLowerCase(),
          tool: tool,
          file_id: fileId
        });
        
        if (response.data.success) {
          toast.success('Starting your download...');
          onSuccess(response.data.download_url);
          onClose();
        }
      } catch (err) {
        console.error('Lead capture error:', err);
        const errorMsg = err.response?.data?.detail || 'Failed to process. Please try again.';
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setInternalLoading(false);
      }
    } else if (onSubmit) {
      // Legacy callback mode
      onSubmit(email);
    }
  };

  // Color variants for different tools
  const colorClasses = {
    indigo: {
      bg: 'bg-indigo-100',
      icon: 'text-indigo-600',
      button: 'bg-indigo-600 hover:bg-indigo-700'
    },
    emerald: {
      bg: 'bg-emerald-100',
      icon: 'text-emerald-600',
      button: 'bg-emerald-600 hover:bg-emerald-700'
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700'
    },
    purple: {
      bg: 'bg-purple-100',
      icon: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700'
    },
    red: {
      bg: 'bg-red-100',
      icon: 'text-red-600',
      button: 'bg-red-600 hover:bg-red-700'
    }
  };

  const colors = colorClasses[accentColor] || colorClasses.indigo;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          data-testid="email-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center mb-6">
          <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <Download className={`w-8 h-8 ${colors.icon}`} />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Download Your Results</h3>
          <p className="text-slate-600 text-sm mt-2">
            Enter your email to download your {toolName} results.
            {rowsProcessed && ` (${rowsProcessed.toLocaleString()} rows processed)`}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="relative">
              <Mail className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-10"
                required
                data-testid="email-capture-input"
              />
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
          
          <Button
            type="submit"
            disabled={isLoading}
            className={`w-full h-12 ${colors.button} text-white`}
            data-testid="email-capture-submit"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Get My File
              </>
            )}
          </Button>
          
          <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              <span>No spam</span>
            </div>
          </div>
        </form>
        
        <div className="mt-6 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 text-center">
            By downloading, you agree to receive occasional emails about 
            CRM Data Cleaner features. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureModal;
